import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Trash2, Plus, ChevronUp, ChevronDown, ImageIcon } from 'lucide-react';

/**
 * UniversalContentEditor
 * ──────────────────────
 * Introspects ANY block.content object and renders generic controls so
 * "Edit Section" works for every premium block type (services, testimonials,
 * features, team, gallery, faq, footer, contact, etc.) without writing a
 * bespoke editor for each.
 *
 * Heuristics:
 *   - String  → <Input> (Textarea if name suggests body copy)
 *   - Array of string  → list with Add/Remove + per-item input
 *   - Array of object  → repeating card with all fields editable inline + Add/Remove
 *   - Nested object   → labelled subgroup
 *   - Image URLs (field name contains 'image_url', or path includes 'images', 'photo') → image input with "Pick" button
 *
 * Caller wires onChange(newContent).
 */

const SHORT_TEXTAREA = new Set(['quote', 'description', 'tagline', 'text', 'body', 'answer']);
const IMAGE_KEYS = new Set(['image_url', 'image', 'background_image', 'map_image', 'photo']);
const KEY_ORDER = ['title', 'eyebrow', 'tagline', 'text', 'description', 'image_url'];
// Section-level meta — surfaced as dedicated controls at the top of the editor,
// NOT in the recursive content walker.
const RESERVED_KEYS = new Set(['section_height', 'attached']);

function isImageKey(key) {
  return IMAGE_KEYS.has(key) || /(_image|image_)/.test(key);
}

function StringField({ label, value, onChange, multiline, testId }) {
  const Cmp = multiline ? Textarea : Input;
  return (
    <div className="mb-3">
      <Label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">{label}</Label>
      <Cmp
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={multiline ? 3 : undefined}
        className="bg-white/5 border-white/10 text-white mt-1 rounded-lg text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function ImageField({ label, value, onChange, onPick, testId }) {
  return (
    <div className="mb-3">
      <Label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">{label}</Label>
      <div className="mt-1 flex gap-2 items-center">
        {value
          ? <img src={value} alt="" className="w-12 h-12 object-cover rounded-md border border-white/10" />
          : <div className="w-12 h-12 rounded-md border border-dashed border-white/15 flex items-center justify-center"><ImageIcon size={14} className="text-white/40" /></div>}
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste image URL"
          className="bg-white/5 border-white/10 text-white rounded-lg text-xs flex-1"
          data-testid={testId}
        />
        {onPick && (
          <button type="button" onClick={onPick} className="px-2.5 py-1.5 rounded-md bg-[#8B5CF6]/20 border border-[#8B5CF6]/40 text-[#A78BFA] text-[10px] uppercase tracking-wider font-bold hover:bg-[#8B5CF6]/30">Pick</button>
        )}
      </div>
    </div>
  );
}

function ArrayOfStrings({ label, value, onChange, basePath }) {
  const arr = value || [];
  const update = (i, v) => { const next = [...arr]; next[i] = v; onChange(next); };
  const remove = (i) => onChange(arr.filter((_, k) => k !== i));
  const add = () => onChange([...arr, '']);
  return (
    <div className="mb-3">
      <Label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">{label}</Label>
      <div className="mt-1 space-y-1.5">
        {arr.map((v, i) => (
          <div key={i} className="flex gap-1.5">
            <Input value={v} onChange={(e) => update(i, e.target.value)} className="bg-white/5 border-white/10 text-white rounded-md text-xs h-8" data-testid={`${basePath}-${i}`} />
            <button type="button" onClick={() => remove(i)} className="px-2 rounded-md bg-red-500/10 border border-red-500/20 hover:bg-red-500/20" data-testid={`${basePath}-rm-${i}`}><Trash2 size={11} className="text-red-300" /></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold flex items-center gap-1 hover:opacity-80" data-testid={`${basePath}-add`}><Plus size={11} /> Add item</button>
      </div>
    </div>
  );
}

function ArrayOfObjects({ label, value, onChange, onMediaPick, basePath, defaultItem }) {
  const arr = value || [];
  const updateItem = (i, k, v) => { const next = [...arr]; next[i] = { ...next[i], [k]: v }; onChange(next); };
  const updateWhole = (i, obj) => { const next = [...arr]; next[i] = obj; onChange(next); };
  const remove = (i) => onChange(arr.filter((_, k) => k !== i));
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= arr.length) return;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...arr, defaultItem || {}]);

  return (
    <div className="mb-3">
      <Label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">{label}</Label>
      <div className="mt-1 space-y-2">
        {arr.map((item, i) => (
          <div key={i} className="border border-white/10 rounded-lg p-2.5 bg-white/[0.02]" data-testid={`${basePath}-item-${i}`}>
            <div className="flex items-center justify-between mb-2 gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold">#{i+1}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i===0} className="p-0.5 hover:bg-white/10 rounded disabled:opacity-30" data-testid={`${basePath}-up-${i}`}><ChevronUp size={12} className="text-white/70" /></button>
                <button type="button" onClick={() => move(i, +1)} disabled={i===arr.length-1} className="p-0.5 hover:bg-white/10 rounded disabled:opacity-30" data-testid={`${basePath}-down-${i}`}><ChevronDown size={12} className="text-white/70" /></button>
                <button type="button" onClick={() => remove(i)} className="p-0.5 hover:bg-red-500/20 rounded" data-testid={`${basePath}-rm-${i}`}><Trash2 size={12} className="text-red-300" /></button>
              </div>
            </div>
            <ObjectFields obj={item} onChange={(k, v) => updateItem(i, k, v)} onReplace={(o) => updateWhole(i, o)} onMediaPick={onMediaPick} basePath={`${basePath}-${i}`} compact />
          </div>
        ))}
        <button type="button" onClick={add} className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold flex items-center gap-1 hover:opacity-80" data-testid={`${basePath}-add`}><Plus size={11} /> Add item</button>
      </div>
    </div>
  );
}

function ObjectFields({ obj, onChange, onMediaPick, basePath, compact }) {
  // Sort with KEY_ORDER preference; unknown keys go after. Reserved meta
  // keys (section_height, attached) are surfaced as dedicated controls above.
  const keys = Object.keys(obj || {}).filter(k => !RESERVED_KEYS.has(k)).sort((a, b) => {
    const ai = KEY_ORDER.indexOf(a); const bi = KEY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1; if (bi === -1) return -1;
    return ai - bi;
  });
  return (
    <>
      {keys.map((k) => {
        const v = obj[k];
        const path = `${basePath}-${k}`;
        if (typeof v === 'string') {
          if (isImageKey(k)) {
            return <ImageField key={k} label={k.replace(/_/g, ' ')} value={v} onChange={(nv) => onChange(k, nv)} onPick={() => onMediaPick?.(path)} testId={path} />;
          }
          const multi = SHORT_TEXTAREA.has(k) || (v && v.length > 60);
          return <StringField key={k} label={k.replace(/_/g, ' ')} value={v} onChange={(nv) => onChange(k, nv)} multiline={multi} testId={path} />;
        }
        if (typeof v === 'number') {
          return (
            <div key={k} className="mb-3">
              <Label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">{k.replace(/_/g, ' ')}</Label>
              <Input type="number" value={v} onChange={(e) => onChange(k, Number(e.target.value))} className="bg-white/5 border-white/10 text-white mt-1 rounded-lg text-sm" data-testid={path} />
            </div>
          );
        }
        if (typeof v === 'boolean') {
          return (
            <div key={k} className="mb-3 flex items-center justify-between">
              <Label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">{k.replace(/_/g, ' ')}</Label>
              <input type="checkbox" checked={v} onChange={(e) => onChange(k, e.target.checked)} data-testid={path} />
            </div>
          );
        }
        if (Array.isArray(v)) {
          if (v.length > 0 && typeof v[0] === 'string') {
            // Special case: pure array of image URLs (gallery images) → image entries
            if (k === 'images' || k === 'gallery') {
              return (
                <div key={k} className="mb-3">
                  <Label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">{k}</Label>
                  <div className="mt-1 space-y-1.5">
                    {v.map((url, i) => (
                      <div key={i} className="flex gap-1.5 items-center">
                        {url ? <img src={url} alt="" className="w-9 h-9 object-cover rounded-md border border-white/10" /> : <div className="w-9 h-9 rounded-md border border-dashed border-white/15" />}
                        <Input value={url} onChange={(e) => { const next = [...v]; next[i] = e.target.value; onChange(k, next); }} placeholder="image URL" className="bg-white/5 border-white/10 text-white rounded-md text-[11px] h-8 flex-1" data-testid={`${path}-${i}`} />
                        <button type="button" onClick={() => onChange(k, v.filter((_, j) => j !== i))} className="px-2 rounded-md bg-red-500/10 border border-red-500/20 hover:bg-red-500/20" data-testid={`${path}-rm-${i}`}><Trash2 size={11} className="text-red-300" /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => onChange(k, [...v, ''])} className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold flex items-center gap-1 hover:opacity-80" data-testid={`${path}-add`}><Plus size={11} /> Add image</button>
                  </div>
                </div>
              );
            }
            return <ArrayOfStrings key={k} label={k} value={v} onChange={(nv) => onChange(k, nv)} basePath={path} />;
          }
          if (v.length === 0 || typeof v[0] === 'object') {
            const defaultItem = v[0] ? Object.keys(v[0]).reduce((acc, kk) => ({ ...acc, [kk]: typeof v[0][kk] === 'string' ? '' : (Array.isArray(v[0][kk]) ? [] : v[0][kk]) }), {}) : {};
            return <ArrayOfObjects key={k} label={k} value={v} onChange={(nv) => onChange(k, nv)} onMediaPick={onMediaPick} basePath={path} defaultItem={defaultItem} />;
          }
        }
        if (v && typeof v === 'object') {
          return (
            <div key={k} className="mb-3 border-l-2 border-white/10 pl-3">
              <p className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold mb-1">{k}</p>
              <ObjectFields obj={v} onChange={(kk, vv) => onChange(k, { ...v, [kk]: vv })} onMediaPick={onMediaPick} basePath={path} compact={compact} />
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

export default function UniversalContentEditor({ block, onChange, onMediaPick, hideSectionMeta = false }) {
  const content = block?.content || {};
  const setField = (k, v) => onChange({ ...content, [k]: v });
  const setWhole = (next) => onChange(next);
  const rootRef = useRef(null);
  const lastFieldRef = useRef(null); // { el, selectionStart, selectionEnd }
  const HEIGHTS = [
    { v: 'compact', label: 'Compact' },
    { v: 'normal',  label: 'Normal' },
    { v: 'tall',    label: 'Tall' },
    { v: 'fullbleed', label: 'Full Bleed' },
  ];
  // Iter 167 — Symbols picker. Track the last focused input/textarea inside
  // this editor so a click on a symbol button inserts at the caret position
  // of whichever field the user was just editing. We use `mousedown` on the
  // button (preventDefault) so focus isn't stolen from the source input.
  const SYMBOLS = ['✓', '✗', '─', '│', '•', '★', '→', '✦', '·', '—'];
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onFocusIn = (e) => {
      const el = e.target;
      if (!el) return;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        lastFieldRef.current = el;
      }
    };
    root.addEventListener('focusin', onFocusIn);
    return () => root.removeEventListener('focusin', onFocusIn);
  }, []);
  const insertSymbol = (sym) => {
    const el = lastFieldRef.current;
    if (!el || !document.body.contains(el)) {
      // Fallback: copy to clipboard so user can paste it where needed.
      try { navigator.clipboard.writeText(sym); } catch { /* noop */ }
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    const next = before + sym + after;
    // Fire a native input event so React's controlled-component handler picks
    // up the new value. We set value via the prototype descriptor to bypass
    // React's synthetic value tracker.
    const proto = el.tagName === 'TEXTAREA'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    setter.call(el, next);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    // Restore caret right after the inserted symbol.
    requestAnimationFrame(() => {
      try {
        const pos = start + sym.length;
        el.setSelectionRange(pos, pos);
        el.focus();
      } catch { /* noop */ }
    });
  };
  return (
    <div className="space-y-1" data-testid={`universal-editor-${block.id}`} ref={rootRef}>
      {!hideSectionMeta && (
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-2">
          {block.type}{block.layout ? ` · ${block.layout}` : ''}
        </p>
      )}

      {/* Iter 167 — Symbols quick-insert row. Always visible so users can
          drop a ✓, ✗ or bullet into any field they last touched. */}
      <div className="border border-white/10 rounded-lg p-2.5 mb-3 bg-white/[0.02]" data-testid="symbols-picker">
        <p className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold mb-1.5">Symbols</p>
        <div className="flex flex-wrap gap-1">
          {SYMBOLS.map((sym) => (
            <button
              key={sym}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertSymbol(sym); }}
              className="w-7 h-7 rounded-md bg-white/5 border border-white/10 text-white text-sm hover:bg-[#8B5CF6]/20 hover:border-[#8B5CF6]/40 hover:text-[#A78BFA] flex items-center justify-center"
              title={`Insert ${sym}`}
              data-testid={`symbol-${sym}`}
            >{sym}</button>
          ))}
        </div>
        <p className="mt-1.5 text-[9px] text-[#64748B] italic leading-snug">Click a field below first, then tap a symbol to insert it.</p>
      </div>

      {/* Section-level controls — only render when not delegated to a host
          drawer that surfaces them in a dedicated "Spacing" accordion. */}
      {!hideSectionMeta && (
        <div className="border border-white/10 rounded-lg p-3 mb-3 bg-white/[0.02]">
          <p className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold mb-2">Section</p>
          <div className="mb-2">
            <label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">Height</label>
            <div className="flex gap-1 mt-1">
              {HEIGHTS.map(h => (
                <button key={h.v} type="button" onClick={() => setField('section_height', h.v)}
                  className={`flex-1 px-1.5 py-1 rounded-md text-[10px] uppercase tracking-wider font-bold border transition-all ${(content.section_height || 'normal') === h.v ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/30' : 'bg-white/5 text-[#64748B] border-white/5 hover:border-white/10'}`}
                  data-testid={`section-height-${h.v}`}>{h.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-[#9CA3AF] text-[11px] uppercase tracking-wider font-bold">Attach to section above</label>
            <input type="checkbox" checked={!!content.attached} onChange={(e) => setField('attached', e.target.checked)} data-testid="section-attached" />
          </div>
          <p className="mt-1 text-[10px] text-[#64748B] italic leading-snug">Removes the visual gap with the previous block so they read as one section.</p>
        </div>
      )}

      <ObjectFields
        obj={content}
        onChange={setField}
        onReplace={setWhole}
        onMediaPick={onMediaPick}
        basePath={`${block.id}`}
      />
    </div>
  );
}
